import React, { useState, useEffect, useCallback } from 'react';
import BulkTeamInviteModal from './BulkTeamInviteModal';

// Owner/manager invite panel: send team invites by email and manage pending
// ones (copy link / resend / revoke). Self-contained — drop into the team page.
// Bulk CSV import (15 July 2026) added alongside the existing single-invite
// flow — same "review before you send" pattern as the introducer bulk import.
const tok = () => localStorage.getItem('token');
const linkFor = (token) => `${window.location.origin}/team-invite/${token}`;

const btn = (bg, color) => ({ padding: '5px 10px', background: bg, color, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 });
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5a6e', margin: '10px 0 4px' };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default function TeamInvitePanel({ managers = [], isManager = false }) {
  const [invites, setInvites] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'adviser', manager_id: '' });
  const [editingInvite, setEditingInvite] = useState(null); // the invite being edited, or null for a new one
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/broker/invites', { headers: { Authorization: `Bearer ${tok()}` } });
      if (res.ok) { const d = await res.json(); setInvites(d.invites || []); }
    } catch (_) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1600); } catch (_) {}
  };

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const body = { name: form.name.trim(), email: form.email.trim(), role: form.role };
      if (form.manager_id) body.manager_id = form.manager_id;
      const url    = editingInvite ? `/api/broker/invites/${editingInvite.id}` : '/api/broker/invite-send';
      const method = editingInvite ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || d.message || (editingInvite ? 'Could not update invite' : 'Could not send invite')); setBusy(false); return; }
      setLastLink({ email: body.email, link: d.acceptLink, edited: !!editingInvite });
      setForm({ name: '', email: '', role: 'adviser', manager_id: '' });
      setEditingInvite(null);
      setShowModal(false);
      load();
    } catch (_) { setError('Network error'); }
    setBusy(false);
  };

  const openNewInvite = () => {
    setError(''); setEditingInvite(null);
    setForm({ name: '', email: '', role: 'adviser', manager_id: '' });
    setShowModal(true);
  };

  const openEdit = (inv) => {
    setError(''); setEditingInvite(inv);
    setForm({ name: inv.name || '', email: inv.email || '', role: inv.role || 'adviser', manager_id: inv.manager_id || '' });
    setShowModal(true);
  };

  const resend = async (id) => {
    try {
      const res = await fetch(`/api/broker/invites/${id}/resend`, { method: 'POST', headers: { Authorization: `Bearer ${tok()}` } });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.acceptLink) copy(d.acceptLink, `resend-${id}`);
    } catch (_) {}
    load();
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite? The link will stop working.')) return;
    try { await fetch(`/api/broker/invites/${id}/revoke`, { method: 'POST', headers: { Authorization: `Bearer ${tok()}` } }); } catch (_) {}
    load();
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0e1b2c' }}>
          Pending invites <span style={{ fontSize: 13, fontWeight: 400, color: '#a0aec0' }}>({invites.length})</span>
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulkModal(true)} style={{ ...btn('#EDF2F7', '#374151'), padding: '7px 14px', fontSize: 13 }}>
            ⬆ Bulk Import
          </button>
          <button onClick={openNewInvite} style={{ ...btn('#0e1b2c', '#fff'), padding: '7px 14px', fontSize: 13 }}>
            ✉️ Invite teammate
          </button>
        </div>
      </div>

      {lastLink && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ fontWeight: 600, color: '#065f46', fontSize: 13, marginBottom: 6 }}>{lastLink.edited ? 'Invite updated for' : 'Invite sent to'} {lastLink.email}</div>
          <div style={{ fontSize: 12, color: '#047857', marginBottom: 8 }}>We emailed them a link. You can also share it directly:</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input readOnly value={lastLink.link} onFocus={e => e.target.select()} style={{ flex: 1, minWidth: 180, fontSize: 12, padding: '8px 10px', border: '1px solid #a7f3d0', borderRadius: 6, color: '#0e1b2c', background: '#fff' }} />
            <button onClick={() => copy(lastLink.link, 'last')} style={btn('#10b981', '#fff')}>{copied === 'last' ? 'Copied!' : 'Copy'}</button>
            <button onClick={() => setLastLink(null)} style={btn('#EDF2F7', '#374151')}>Dismiss</button>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
        {invites.length === 0
          ? <div style={{ padding: '20px 16px', color: '#a0aec0', fontSize: 13 }}>No pending invites. Invite a teammate to give them a login.</div>
          : invites.map(inv => (
            <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F7FAFC', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#0e1b2c', fontSize: 14 }}>
                  {inv.name}
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '1px 7px', borderRadius: 9999, marginLeft: 6 }}>
                    {inv.role}{inv.manager_name ? ` · under ${inv.manager_name}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#718096' }}>{inv.email} · expires {new Date(inv.expires_at).toLocaleDateString('en-GB')}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => openEdit(inv)} style={btn('#EDF2F7', '#374151')}>Edit</button>
                <button onClick={() => copy(linkFor(inv.token), `copy-${inv.id}`)} style={btn('#e0f2fe', '#075985')}>{copied === `copy-${inv.id}` ? 'Copied!' : 'Copy link'}</button>
                <button onClick={() => resend(inv.id)} style={btn('#EDF2F7', '#374151')}>{copied === `resend-${inv.id}` ? 'Link copied' : 'Resend'}</button>
                <button onClick={() => revoke(inv.id)} style={btn('#fee2e2', '#991b1b')}>Revoke</button>
              </div>
            </div>
          ))
        }
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }} onClick={() => { setShowModal(false); setEditingInvite(null); }}>
          <form onClick={e => e.stopPropagation()} onSubmit={submit} style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 420 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#0e1b2c' }}>{editingInvite ? 'Edit invite' : 'Invite a teammate'}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#718096' }}>{editingInvite ? "Fix a mistyped name, email, role or manager — we'll re-send with a fresh link." : "They'll get an email to set their own password and sign in."}</p>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <label style={lbl}>Full name</label>
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
            <label style={lbl}>Email</label>
            <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inp} />
            <label style={lbl}>Role</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inp}>
              <option value="adviser">Adviser</option>
              <option value="manager">Manager</option>
              {/* Admin: full company access like an owner, but not a director
                  (no billing/agreement authority) — owner/admin only, same
                  restriction the backend enforces. See
                  ROLES-PERMISSIONS-TIDYUP-SCOPE.md §5/§7. */}
              {!isManager && <option value="admin">Admin</option>}
            </select>
            <label style={lbl}>Reports to {(form.role === 'manager' || form.role === 'admin') ? '(optional)' : '(manager)'}</label>
            <select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))} style={inp}>
              <option value="">— No manager —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => { setShowModal(false); setEditingInvite(null); }} style={{ flex: 1, padding: '10px', background: '#EDF2F7', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={busy} style={{ flex: 1, padding: '10px', background: busy ? '#a7f3d0' : '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>{busy ? (editingInvite ? 'Saving…' : 'Sending…') : (editingInvite ? 'Save changes' : 'Send invite')}</button>
            </div>
          </form>
        </div>
      )}

      {showBulkModal && (
        <BulkTeamInviteModal
          isManager={isManager}
          onClose={() => setShowBulkModal(false)}
          onSuccess={load}
        />
      )}
    </div>
  );
}
